import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Stack, Tooltip } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { LiveListEntity } from '@/types/live-lists'
import { BaseVendor } from '@/types/relay'
import { VendorAlias } from '@/types/vendor-settings'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'

export type FormValues = {
  vendor_name: string
  remit_address: string
  vendor_address: string
}

type QBOAddVendorAliasDialogProps = {
  overlay: OverlayState
  vendor: LiveListEntity<BaseVendor>
  orgId: string
  defaultValues?: FormValues
  disabledSaveHelperText?: string
  onAdd: (newAlias: VendorAlias) => void
}

export default function AddVendorAliasDialog({
  overlay,
  vendor,
  orgId,
  defaultValues,
  disabledSaveHelperText,
  onAdd,
}: QBOAddVendorAliasDialogProps) {
  const formDefaultValues = useMemo(
    () => ({
      vendor_address: defaultValues?.vendor_address || '',
      remit_address: defaultValues?.remit_address || '',
      vendor_name: defaultValues?.vendor_name || '',
    }),
    [defaultValues],
  )

  const methods = useForm<FormValues>({
    defaultValues: formDefaultValues,
  })

  const { isValid, isDirty } = methods.formState

  const saveIsDisabled = disabledSaveHelperText
    ? !isDirty || !isValid
    : !isValid

  function handleOnSubmit(values: FormValues) {
    onAdd({
      org_id: orgId,
      native_vendor_id: vendor.entity_id,
      ...values,
    })
  }

  useEffect(() => {
    if (overlay.isOpen) {
      methods.reset(formDefaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to reset when the dialog is opened
  }, [methods, overlay.isOpen])

  return (
    <Dialog title="Add Vendor Alias" {...overlay} maxWidth="xs">
      <PixyDocsForm methods={methods} onSubmit={handleOnSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <FormTextField
              name="vendor_name"
              label="Vendor Name"
              required
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              useControllerProps={{
                rules: { validate: (value) => value.trim().length > 0 },
              }}
            />
            <FormTextField
              name="remit_address"
              label="Remit Address"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
            <FormTextField
              name="vendor_address"
              label="Vendor Address"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Tooltip
            enterDelay={500}
            title={saveIsDisabled ? disabledSaveHelperText : undefined}
          >
            {/* div needed to wrap Button to properly apply Tooltip when button is disabled */}
            <div>
              <Button type="submit" disabled={saveIsDisabled}>
                Save
              </Button>
            </div>
          </Tooltip>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
